import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createTextVNode as _createTextVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = ["open"]
const _hoisted_2 = { class: "flex min-h-[72px] h-full cursor-pointer list-none items-center rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#8b6536] [&::-webkit-details-marker]:hidden" }
const _hoisted_3 = { class: "min-w-0 flex-1 px-[18px] py-3.5" }
const _hoisted_4 = { class: "m-0 text-sm tracking-tight text-gray-900 font-ivymode" }
const _hoisted_5 = { class: "m-0 mt-1 text-[10px] leading-[15px] font-normal tracking-[0.1px] text-parameter-text truncate" }
const _hoisted_6 = {
  key: 0,
  class: "relative shrink-0 w-28 sm:w-36 overflow-hidden bg-parameter-bg"
}
const _hoisted_7 = ["src", "srcset"]
const _hoisted_8 = {
  key: 0,
  class: "lg:hidden pointer-events-none col-span-5 md:col-start-1 md:row-start-1 w-full md:w-(--image-width) mx-auto max-w-64 md:mx-0 md:max-w-42 aspect-square self-start z-10 h-full flex items-start overflow-hidden"
}
const _hoisted_9 = ["src"]
const _hoisted_10 = { class: "col-span-5 md:col-start-1 md:row-start-1 z-20 flex flex-col justify-start items-center md:items-end" }
const _hoisted_11 = { class: "mt-2 grow flex flex-col items-end justify-start w-full md:w-[calc(105%-var(--image-width))] lg:w-11/12" }
const _hoisted_12 = {
  key: 0,
  class: "flex flex-col w-full mt-5"
}
const _hoisted_13 = {
  key: 0,
  class: "w-full overflow-hidden"
}
const _hoisted_14 = { class: "flex flex-col w-full mt-5" }
const _hoisted_15 = {
  key: 1,
  class: "my-3 mr-auto flex w-full md:max-w-4/5 gap-x-4 items-stretch rounded-[15px] bg-[#f3f0e9] p-3 max-h-20"
}
const _hoisted_16 = { class: "aspect-square h-full shrink-0 rounded-2xl overflow-hidden max-h-16" }
const _hoisted_17 = ["src"]
const _hoisted_18 = {
  key: 0,
  class: "m-0 text-[12px] leading-3.5 font-bold text-parameter-title"
}
const _hoisted_19 = {
  key: 1,
  class: "mt-1 text-[9px] leading-[13px] text-parameter-text relative overflow-hidden h-10"
}
const _hoisted_20 = { key: 2 }
const _hoisted_21 = {
  key: 1,
  class: "absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 rounded-[11px] bg-parameter-box-bg"
}

import {onUpdated, ref} from "vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'itemV3ParameterBox',
  props: {
    step: { default: 1 },
    title: { default: '' },
    image: { default: '' },
    headerImage: { default: '' },
    headerImageSrcset: { default: '' },
    imageWidthPercent: { default: 30 },
    subtitle: { default: '' },
    tipTitle: { default: '' },
    tipText: { default: '' },
    tipImage: { default: '' },
    loading: { type: Boolean, default: true },
    open: { type: Boolean, default: false }
  },
  emits: ["update:open"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const handleToggle = (event: Event) => {
  emit('update:open', (event.target as HTMLDetailsElement).open);
};

// iOS/iPadOS Safari (WebKit) bug: when the DOM content inside an already-open <details>
// element is heavily mutated (nodes added/removed, not just text updates), WebKit silently
// drops the "open" attribute, closing the box even though our "open" prop/ref is still true.
// Desktop browsers (Blink/Gecko) don't have this defect. To work around it we keep a direct
// reference to the <details> element and re-assert its "open" state right after every DOM
// update of this component, which also covers updates caused by reactive data used inside
// the "main"/"more"/"details" slots (e.g. loading state or variant lists toggling).
const detailsRef = ref<HTMLDetailsElement | null>(null);

onUpdated(() => {
  if (detailsRef.value && detailsRef.value.open !== props.open) {
    detailsRef.value.open = props.open;
  }
});

const showMore = ref(false);

const toggleMore = () => {
  showMore.value = !showMore.value;
};

const onBeforeEnter = (el: Element) => {
  (el as HTMLElement).style.height = '0px';
};

const onEnter = (el: Element) => {
  const target = el as HTMLElement;
  requestAnimationFrame(() => {
    target.style.transition = 'height 300ms ease';
    target.style.height = `${target.scrollHeight}px`;
  });
};

const onAfterEnter = (el: Element) => {
  const target = el as HTMLElement;
  target.style.transition = '';
  target.style.height = 'auto';
};

const onBeforeLeave = (el: Element) => {
  const target = el as HTMLElement;
  target.style.height = `${target.scrollHeight}px`;
};

const onLeave = (el: Element) => {
  const target = el as HTMLElement;
  requestAnimationFrame(() => {
    target.style.transition = 'height 300ms ease';
    target.style.height = '0px';
  });
};

const onAfterLeave = (el: Element) => {
  (el as HTMLElement).style.transition = '';
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("details", {
    ref_key: "detailsRef",
    ref: detailsRef,
    open: __props.open,
    class: "group/parameter w-full rounded-2xl border border-white/70 bg-parameter-box-bg text-parameter-text shadow-[0_1px_3px_rgba(68,68,68,0.06)]",
    onToggle: handleToggle
  }, [
    _createElementVNode("summary", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h5", _hoisted_4, _toDisplayString(__props.title), 1),
        _createElementVNode("p", _hoisted_5, _toDisplayString(__props.subtitle), 1)
      ]),
      (__props.headerImage)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("img", {
              src: __props.headerImage,
              srcset: __props.headerImageSrcset || undefined,
              class: "w-full h-full object-cover",
              alt: ""
            }, null, 8, _hoisted_7),
            _cache[0] || (_cache[0] = _createElementVNode("div", { class: "pointer-events-none absolute inset-0 bg-gradient-to-r from-parameter-box-bg to-transparent w-1/4" }, null, -1))
          ]))
        : _createCommentVNode("", true),
      _cache[1] || (_cache[1] = _createElementVNode("div", { class: "shrink-0 px-2 py-2.5" }, [
        _createElementVNode("svg", {
          "aria-hidden": "true",
          viewBox: "0 0 12 12",
          fill: "none",
          class: "size-3 text-[#b7a98f] transition-transform duration-300 group-open/parameter:rotate-90"
        }, [
          _createElementVNode("path", {
            d: "m4 1.5 4 4.5-4 4.5",
            stroke: "currentColor",
            "stroke-linecap": "round",
            "stroke-linejoin": "round"
          })
        ])
      ], -1))
    ]),
    _createElementVNode("div", {
      class: "relative isolate grid min-h-[263px] grid-cols-1 md:grid-cols-5 px-3.5",
      style: _normalizeStyle({ '--image-width': `clamp(0%, ${__props.imageWidthPercent}%, 100%)` })
    }, [
      (__props.image && __props.imageWidthPercent > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("img", {
              src: __props.image,
              class: "relative md:-left-5 w-full object-contain object-center lazy",
              alt: ""
            }, null, 8, _hoisted_9)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("div", _hoisted_11, [
          _renderSlot(_ctx.$slots, "main"),
          (_ctx.$slots.more && !showMore.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                class: "flex w-full items-center gap-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536] mt-2",
                "aria-expanded": "false",
                onClick: toggleMore
              }, [...(_cache[2] || (_cache[2] = [
                _createStaticVNode("<span class=\"h-px flex-1 bg-[#e7ddcc]\"></span><span class=\"flex items-center gap-1.5 text-xs font-medium tracking-[0.1px] whitespace-nowrap text-parameter-text cursor-pointer\"> Mehr anzeigen <svg aria-hidden=\"true\" viewBox=\"0 0 12 12\" fill=\"none\" class=\"size-2.5 shrink-0 text-[#555956]\"><path d=\"m1.5 4 4.5 4 4.5-4\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-linejoin=\"round\"></path></svg></span><span class=\"h-px flex-1 bg-[#e7ddcc]\"></span>", 3)
              ]))]))
            : _createCommentVNode("", true)
        ]),
        (_ctx.$slots.details)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _renderSlot(_ctx.$slots, "details")
            ]))
          : _createCommentVNode("", true),
        _createVNode(_Transition, {
          onBeforeEnter: onBeforeEnter,
          onEnter: onEnter,
          onAfterEnter: onAfterEnter,
          onBeforeLeave: onBeforeLeave,
          onLeave: onLeave,
          onAfterLeave: onAfterLeave
        }, {
          default: _withCtx(() => [
            (showMore.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  _createElementVNode("div", _hoisted_14, [
                    _renderSlot(_ctx.$slots, "more"),
                    _createElementVNode("button", {
                      type: "button",
                      class: "mt-4 flex w-full items-center gap-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]",
                      "aria-expanded": "true",
                      onClick: toggleMore
                    }, [...(_cache[3] || (_cache[3] = [
                      _createElementVNode("span", { class: "h-px flex-1 bg-[#e7ddcc]" }, null, -1),
                      _createElementVNode("span", { class: "flex items-center gap-1.5 text-xs font-medium tracking-[0.1px] whitespace-nowrap text-parameter-text" }, [
                        _createTextVNode(" Weniger anzeigen "),
                        _createElementVNode("svg", {
                          "aria-hidden": "true",
                          viewBox: "0 0 12 12",
                          fill: "none",
                          class: "size-2.5 shrink-0 rotate-180 text-[#555956]"
                        }, [
                          _createElementVNode("path", {
                            d: "m1.5 4 4.5 4 4.5-4",
                            stroke: "currentColor",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                          })
                        ])
                      ], -1),
                      _createElementVNode("span", { class: "h-px flex-1 bg-[#e7ddcc]" }, null, -1)
                    ]))])
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 3
        }),
        (__props.tipTitle || __props.tipText)
          ? (_openBlock(), _createElementBlock("aside", _hoisted_15, [
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("img", {
                  src: __props.tipImage,
                  class: "h-full w-full object-cover object-center",
                  alt: ""
                }, null, 8, _hoisted_17)
              ]),
              _createElementVNode("div", null, [
                (__props.tipTitle)
                  ? (_openBlock(), _createElementBlock("h6", _hoisted_18, _toDisplayString(__props.tipTitle), 1))
                  : _createCommentVNode("", true),
                (__props.tipText)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(__props.tipText), 1))
                  : _createCommentVNode("", true)
              ])
            ]))
          : (_openBlock(), _createElementBlock("aside", _hoisted_20, [...(_cache[4] || (_cache[4] = [
              _createElementVNode("br", null, null, -1)
            ]))]))
      ]),
      (__props.loading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_21, [...(_cache[5] || (_cache[5] = [
            _createStaticVNode("<span class=\"relative flex size-14 items-center justify-center\"><span class=\"absolute size-full rounded-full bg-radial-[at_35%_30%] from-[#a57a43] to-[#805b2f] opacity-25 animate-ping\"></span><span class=\"absolute size-11 rounded-full border-[3px] border-[#e7ddcc] border-t-[#8b6536] animate-spin\"></span><span class=\"size-4 rounded-full bg-radial-[at_35%_30%] from-[#a57a43] to-[#805b2f] animate-pulse\"></span></span><span class=\"text-[10px] font-medium tracking-[0.1px] text-parameter-text animate-pulse\"> Wird geladen … </span>", 2)
          ]))]))
        : _createCommentVNode("", true)
    ], 4)
  ], 40, _hoisted_1))
}
}

})